import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, Result, SectionTitle, Image, Separator, CorrectionBox, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider } from '../../../../components/course/CourseComponents';
import screen2 from '../screen2.jpg';
import screen3 from '../screen3.jpg';
import screen4 from '../screen4.jpg';

const Chapitre6Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Augmenter le score à chaque fois que l'oiseau passe entre 2 tuyaux, et l'afficher
        </p>
      </InfoBox>

      <Separator type="text" text="Calcul du score" color="gray-darker" size="md" />
      <SectionTitle>📊 1/ Calculer le score</SectionTitle>

      <p>On souhaite que la variable globale <InlineCode>score</InlineCode> s'incrémente de 1 à chaque fois que l'oiseau passe une paire de tuyaux.<br/>
      Pour le moment, on ne teste pas les collisions entre l'oiseau et les tuyaux, donc le score sera augmenté même si l'oiseau passe à travers un tuyau.</p>

      <p>Pour savoir si l'oiseau a passé une paire de tuyaux nommée <InlineCode>t</InlineCode>, on vérifie que <InlineCode>oiseau.x</InlineCode> est supérieur à <InlineCode>t.x</InlineCode> :</p>

      <CodeBlock language="javascript">
{`if (oiseau.x > t.x) {
  score++;
}`}
      </CodeBlock>

      <p>On fait cette vérification pour chaque tuyau de la liste <InlineCode>tuyaux</InlineCode>.</p>

      <Note type="warning" title="⚠️ Problème">
        <p>Le problème, c'est qu'une fois que l'oiseau a passé le tuyau, la condition sera toujours vraie tant que le tuyau ne sera pas supprimé… Le score va donc augmenter plusieurs fois pour un même tuyau !</p>
      </Note>

      <p>Il faut une variable qui indique si le tuyau a déjà été compté dans le score.<br/>
      C'est là qu'intervient la variable <InlineCode>franchi</InlineCode>, présente depuis le début dans l'objet paireTuyaux.</p>

      <p>Tant que <InlineCode>franchi == false</InlineCode>, on teste si l'oiseau a dépassé le tuyau.<br/>
      S'il le dépasse, on augmente le score et on passe <InlineCode>franchi</InlineCode> à <InlineCode>true</InlineCode> pour ne plus tester ce tuyau :</p>

      <CodeBlock language="javascript">
{`// Vérifie si l'oiseau vient de passer le tuyau
if (t.franchi == false && oiseau.x > t.x) {
  score = score + 1;
  t.franchi = true;
}`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : augmentation du score">
        <p>Crée une variable globale <InlineCode>score = 0</InlineCode> :</p>
        <CodeBlock language="javascript">
{`let score = 0;`}
        </CodeBlock>

        <p>Dans la boucle qui parcourt tous les tuyaux, vérifie que le tuyau n'a pas encore été franchi,<br/>
        et si <InlineCode>oiseau.x &gt; tuyau.x</InlineCode>, alors incrémente le score et passe <InlineCode>franchi</InlineCode> à <InlineCode>true</InlineCode>, pour ne plus le tester à l'avenir :</p>

        <CodeBlock language="javascript">
{`// Vérifie si l'oiseau vient de passer le tuyau
if (t.franchi == false && oiseau.x > t.x) {
  score = score + 1;
  t.franchi = true;
}`}
        </CodeBlock>

        <p>Enfin, à la fin de <InlineCode>draw()</InlineCode>, affiche la variable score pour vérifier si tout fonctionne :</p>

        <CodeBlock language="javascript">
{`print(score);`}
        </CodeBlock>

        <br/>
        <Result>À chaque fois que l'oiseau passe un tuyau, le score augmente de 1.</Result>

        <CorrectionBox>
          <CodeBlock language="javascript">
{`// Parcours la liste de tuyaux
for (let i = tuyaux.length - 1; i >= 0; i--) {
  let t = tuyaux[i];

  //...

  // Vérifie si l'oiseau vient de passer le tuyau
  if (t.franchi == false && oiseau.x > t.x) {
    score = score + 1;
    t.franchi = true;
  }
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <Separator type="text" text="Affichage du score" color="gray-darker" size="md" />
      <SectionTitle>💯 2/ Affichage du score</SectionTitle>

      <p>On va maintenant afficher le score directement sur le canvas, en haut au milieu :</p>

      <CodeBlock language="javascript">
{`textAlign(CENTER, TOP); // aligné au centre en haut
textSize(64);            // taille du texte en grand
textStyle(BOLD);         // texte en gras
fill(255);               // texte en blanc
text(score, width / 2, 20); // affichage au centre, en haut`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : affichage du score">
        <p>À la fin de <InlineCode>draw()</InlineCode>, affiche le score en haut au milieu, en grand, en gras et en blanc.<br/>
        Supprime le <InlineCode>print(score)</InlineCode> qui nous servait au débogage.</p>

        <CodeBlock language="javascript">
{`// Affichage du score
textAlign(CENTER, TOP);
textSize(64);
textStyle(BOLD);
fill(255);
text(score, width / 2, 20);`}
        </CodeBlock>

        <br/>
        <Result>Le score s'affiche sur le canvas.</Result>

      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 3/ Code complet : Affichage du score</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;
let imageOiseau;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyauVitesse = 150; // Vitesse de déplacement des tuyaux
let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes
let oiseauGravite = 800; // Gravité à laquelle l'oiseau tombe
let oiseauImpulsion = -300; // Force d'impulsion quand on clique

let tuyauTemps = 0;
let tuyaux = [];
let score = 0;

// Oiseau
let oiseau = {
  x: 150,
  y: 250,
  vitesseY: 0
};

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
  imageOiseau = loadImage("flappy1.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
  let dt = deltaTime / 1000;

  // Parcours la liste de tuyaux
  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

    // Déplace et affiche le tuyau
    t.x = t.x - tuyauVitesse * dt;
    afficherTuyaux(t);

    // Supprime s'il est complètement à gauche
    if (t.x + imageTuyauHaut.width < 0) {
      tuyaux.splice(i, 1);
    }

    // Vérifie si l’oiseau vient de passer le tuyau
    if (t.franchi == false &&  oiseau.x > t.x) {
      score = score + 1;
      t.franchi = true;
    }
  }

  // Créer un nouveau tuyau toutes les x secondes
  tuyauTemps = tuyauTemps - dt;
  if (tuyauTemps <= 0) {
    tuyauTemps = tuyauIntervalle;
    let positionX = width;
    let positionY = random(100, 400);
    tuyaux.push({ x: positionX, y: positionY, franchi: false });
  }

  // Déplacement de l'oiseau
  oiseau.vitesseY = oiseau.vitesseY + oiseauGravite * dt;
  oiseau.y = oiseau.y + oiseau.vitesseY * dt;
  image(imageOiseau, oiseau.x, oiseau.y);

  // Affichage du score
  textAlign(CENTER, TOP);
  textSize(64);
  textStyle(BOLD);
  fill(255);
  text(score, width / 2, 20);
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  oiseau.vitesseY = oiseauImpulsion;
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}`}
      </CodeBlock>
    </>
  );
};

export default Chapitre6Content; 